import type {
  BillingAuthorization,
  BillingAuthorizationSourcesResponse,
  BillingAuthorizationsResponse,
  UpdateBillingAuthorization,
  UpsertBillingAuthorization,
} from '@scrambl/shared'

import { BaseApi } from './base-api'
import { createSingletonApi } from './singleton-factory'

class BillingAuthorizationApiService extends BaseApi<
  BillingAuthorization,
  UpsertBillingAuthorization,
  UpdateBillingAuthorization
> {
  constructor() {
    super({
      baseEndpoint: '/billing-authorizations',
      responseNormalization: true,
    })
  }

  listAuthorizations(
    params: {
      /** One client id or several (comma-joined on the wire). */
      entityId?: string | string[]
      projectId?: string
      contractId?: string
      ownerId?: string
      status?: string | string[]
      search?: string
      page?: number
      limit?: number
    } = {},
  ): Promise<BillingAuthorizationsResponse> {
    const { status, entityId, ...rest } = params
    const normalized: Record<string, unknown> = { ...rest }
    if (Array.isArray(entityId)) {
      if (entityId.length > 0) normalized.entityId = entityId.join(',')
    } else if (entityId) {
      normalized.entityId = entityId
    }
    if (Array.isArray(status)) {
      if (status.length > 0) normalized.status = status.join(',')
    } else if (status) {
      normalized.status = status
    }
    const query = this.buildQueryParams(normalized)
    return this.makeRequest<BillingAuthorizationsResponse>(
      `${this.config.baseEndpoint}${query}`,
    )
  }

  getEligible(params: {
    entityId: string
    projectId?: string | null
    contractId?: string | null
    invoiceDate?: string
  }): Promise<BillingAuthorization[]> {
    const query = this.buildQueryParams(params)
    return this.makeRequest<BillingAuthorization[]>(
      `${this.config.baseEndpoint}/eligible${query}`,
    )
  }

  getSources(
    id: string,
    params: { page?: number; limit?: number; type?: 'time' | 'expense' } = {},
  ): Promise<BillingAuthorizationSourcesResponse> {
    const query = this.buildQueryParams(params)
    return this.makeRequest<BillingAuthorizationSourcesResponse>(
      `${this.config.baseEndpoint}/${id}/sources${query}`,
    )
  }

  recompute(id: string): Promise<BillingAuthorization> {
    return this.makeRequest<BillingAuthorization>(
      `${this.config.baseEndpoint}/${id}/recompute`,
      { method: 'POST' },
    )
  }
}

export const billingAuthorizationApi = createSingletonApi(
  BillingAuthorizationApiService,
)
